import React, { useEffect, useState } from "react";
import {
  FaSeedling,
  FaPlay,
  FaClock,
  FaEye,
  FaThumbsUp,
  FaYoutube,
  FaSearch,
  FaTimes,
  FaMicrophone,
} from "react-icons/fa";
import "./FarmingVideos.css";

const FarmingVideos = () => {
  const [farmingVideos, setFarmingVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [currentQuery, setCurrentQuery] = useState("farming techniques india hindi");
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Farming-related keywords for suggestion filtering
  const farmingKeywords = [
    "farming", "agriculture", "crop", "harvest", "irrigation", "soil", "fertilizer",
    "tractor", "plowing", "sowing", "cultivation", "organic farming", "livestock",
    "poultry", "dairy", "agronomy", "horticulture", "aquaculture", "sericulture",
    "wheat", "rice", "maize", "pesticides", "compost", "manure", "hydroponics",
    "greenhouse", "vermicomposting", "crop rotation", "sustainable agriculture",
    "integrated pest management", "drought-resistant crops", "precision agriculture",
    "mulching", "crop diseases", "agribusiness", "agroforestry"
  ];

  // Fetch videos
  useEffect(() => {
    const fetchVideos = async () => {
      if (farmingVideos.length > 0) {
        setIsSearching(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/farming-videos?query=${encodeURIComponent(currentQuery)}`
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to fetch videos");
        }
        const data = await response.json();
        setFarmingVideos(data);
      } catch (err) {
        setError(err.message);
        setFarmingVideos([]);
      } finally {
        setLoading(false);
        setIsSearching(false);
      }
    };
    fetchVideos();
  }, [currentQuery]);

  // Fetch YouTube suggestions and filter for farming-related terms
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchTerm.length > 2) {
        try {
          const response = await fetch(
            `https://suggestqueries.google.com/complete/search?client=firefox&ds=yt&q=${encodeURIComponent(searchTerm)}`
          );
          const data = await response.json();
          const filteredSuggestions = (data[1] || []).filter((suggestion) =>
            farmingKeywords.some((keyword) =>
              suggestion.toLowerCase().includes(keyword)
            )
          );
          setSuggestions(filteredSuggestions);
        } catch (err) {
          console.error("Error fetching suggestions:", err);
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
      }
    };
    fetchSuggestions();
  }, [searchTerm]);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    if (searchTerm.trim()) {
      setCurrentQuery(searchTerm);
      setSearchTerm("");
      setSuggestions([]);
    }
  };

  const handleMicClick = () => {
    // Placeholder for future STT functionality
    alert("Speech-to-text feature coming soon! Type your query for now.");
    // Future STT integration: Call your STT API and setSearchTerm with the result
    // Example:
    // navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
    //   // Process audio with STT API and set searchTerm
    // });
  };

  const openVideoModal = (video) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  return (
    <div
      style={{
        fontFamily: "Poppins, sans-serif",
        backgroundColor: "#f0f8f0",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* HEADER */}
      <div
        className="farming-videos-header"
        style={{
          background: "linear-gradient(90deg, #2e7d32 0%, #1b5e20 100%)",
          color: "white",
          padding: "60px 20px",
          textAlign: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        <h1
          style={{
            fontSize: "clamp(28px, 5vw, 48px)",
            marginBottom: "12px",
            fontWeight: "700",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <FaSeedling style={{ fontSize: "clamp(28px, 5vw, 50px)" }} /> Farming Education Videos
        </h1>
        <p
          style={{
            fontSize: "clamp(14px, 2vw, 20px)",
            opacity: "0.9",
            maxWidth: "750px",
            margin: "auto",
          }}
        >
          Discover the <strong>best farming techniques</strong> and{" "}
          <strong>agricultural practices</strong> through expert video tutorials
          for Indian farmers. Watch videos directly on our platform!
        </p>
      </div>

      {/* SEARCH BAR */}
      <div className="farming-videos-search" style={{ padding: "30px 20px", maxWidth: "800px", margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        <form onSubmit={handleSearch} style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search farming videos"
            style={{
              flex: 1,
              padding: "14px 80px 14px 20px", // Adjusted padding for two buttons
              fontSize: "18px",
              borderRadius: "30px",
              border: "1px solid #ccc",
              boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
              outline: "none",
              transition: "border-color 0.3s",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#2e7d32"; }}
            onBlur={(e) => { e.target.style.borderColor = "#ccc"; }}
          />
          <button
            type="button"
            onClick={handleMicClick}
            disabled={isSearching}
            style={{
              position: "absolute",
              right: "50px", // Positioned to the left of the search button
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: isSearching ? "not-allowed" : "pointer",
              fontSize: "24px",
              color: "#2e7d32",
              transition: "color 0.3s",
            }}
            onMouseEnter={(e) => { if (!isSearching) e.currentTarget.style.color = "#1b5e20"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#2e7d32"; }}
            title="Speech-to-text (coming soon)"
          >
            <FaMicrophone />
          </button>
          <button
            type="submit"
            disabled={isSearching}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: isSearching ? "not-allowed" : "pointer",
              fontSize: "24px",
              color: "#2e7d32",
              transition: "color 0.3s",
            }}
            onMouseEnter={(e) => { if (!isSearching) e.currentTarget.style.color = "#1b5e20"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#2e7d32"; }}
          >
            <FaSearch />
          </button>
        </form>
        {error && (
          <div style={{ color: "red", fontSize: "16px", marginTop: "10px", textAlign: "center" }}>
            {error}
          </div>
        )}
        {suggestions.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              background: "white",
              border: "1px solid #ccc",
              borderRadius: "20px",
              marginTop: "8px",
              padding: "10px",
              maxHeight: "250px",
              overflowY: "auto",
              boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              position: "absolute",
              width: "calc(100% - 40px)",
              maxWidth: "760px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
            }}
          >
            {suggestions.map((sug, idx) => (
              <li
                key={idx}
                onClick={() => {
                  setSearchTerm(sug);
                  setCurrentQuery(sug);
                  setSuggestions([]);
                }}
                style={{
                  padding: "14px 20px",
                  cursor: "pointer",
                  borderBottom: idx < suggestions.length - 1 ? "1px solid #eee" : "none",
                  transition: "background 0.3s",
                  fontSize: "16px",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f0f8f0"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "white"; }}
              >
                {sug}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* LOADING INDICATOR FOR SEARCH */}
      {isSearching && (
        <div style={{ textAlign: "center", padding: "20px", fontSize: "18px", color: "#2e7d32" }}>
          Searching for new videos...
        </div>
      )}

      {/* VIDEOS GRID */}
      <div style={{ padding: "40px 20px", flexGrow: 1 }}>
        <h2
          style={{
            color: "#1b5e20",
            marginBottom: "40px",
            fontWeight: "600",
            fontSize: "28px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            textAlign: "center",
            justifyContent: "center",
          }}
        >
          <FaYoutube color="#ff0000" style={{ fontSize: "32px" }} /> Featured Farming Videos
        </h2>

        {farmingVideos.length === 0 && !isSearching ? (
          <div style={{ textAlign: "center", fontSize: "20px", color: "#555" }}>
            No videos found. Try a different search term.
          </div>
        ) : (
          <div
            className="farming-videos-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "30px",
              maxWidth: "1400px",
              margin: "0 auto",
            }}
          >
            {farmingVideos.map((video, index) => (
              <div
                key={index}
                onClick={() => openVideoModal(video)}
                style={{
                  background: "#fff",
                  borderRadius: "20px",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
                  e.currentTarget.style.boxShadow = "0 15px 30px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.1)";
                }}
              >
                <div
                  style={{
                    position: "relative",
                    height: "180px",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.3s ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.05)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "rgba(255, 0, 0, 0.8)",
                      borderRadius: "50%",
                      width: "70px",
                      height: "70px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "28px",
                      color: "white",
                      transition: "background-color 0.3s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255, 0, 0, 1)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(255, 0, 0, 0.8)"; }}
                  >
                    <FaPlay />
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "12px",
                      right: "12px",
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                      color: "white",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    {video.duration}
                  </div>
                </div>
                <div style={{ padding: "20px" }}>
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#1b5e20",
                      marginBottom: "10px",
                      lineHeight: "1.4",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {video.title}
                  </h3>
                  <p
                    style={{
                      fontSize: "15px",
                      color: "#555",
                      lineHeight: "1.5",
                      marginBottom: "15px",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {video.description}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginBottom: "12px",
                    }}
                  >
                    <img
                      src={video.channelThumbnail}
                      alt={video.channel}
                      style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                      }}
                    />
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#666",
                        fontWeight: "500",
                      }}
                    >
                      {video.channel}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingTop: "12px",
                      borderTop: "1px solid #eee",
                      fontSize: "13px",
                      color: "#666",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <FaEye /> {video.views} views
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <FaThumbsUp /> {video.likes} likes
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <FaClock /> {video.duration}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* VIDEO MODAL */}
      {selectedVideo && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "20px",
              width: "min(900px, 95vw)",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "20px",
              position: "relative",
              boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
            }}
          >
            <button
              onClick={closeVideoModal}
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                background: "none",
                border: "none",
                fontSize: "24px",
                color: "#2e7d32",
                cursor: "pointer",
                transition: "color 0.3s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#1b5e20"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#2e7d32"; }}
            >
              <FaTimes />
            </button>
            <h3
              style={{
                fontSize: "22px",
                fontWeight: "600",
                color: "#1b5e20",
                marginBottom: "20px",
                textAlign: "center",
              }}
            >
              {selectedVideo.title}
            </h3>
            <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
              <iframe
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: "12px",
                }}
                src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={selectedVideo.title}
              />
            </div>
            <div
              style={{
                marginTop: "20px",
                fontSize: "16px",
                color: "#555",
                lineHeight: "1.5",
                maxHeight: "100px",
                overflowY: "auto",
              }}
            >
              {selectedVideo.description}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "15px",
                fontSize: "14px",
                color: "#666",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <img
                  src={selectedVideo.channelThumbnail}
                  alt={selectedVideo.channel}
                  style={{ width: "30px", height: "30px", borderRadius: "50%" }}
                />
                <span>{selectedVideo.channel}</span>
              </div>
              <div style={{ display: "flex", gap: "20px" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <FaEye /> {selectedVideo.views} views
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <FaThumbsUp /> {selectedVideo.likes} likes
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div
        className="farming-videos-footer"
        style={{
          backgroundColor: "#2e7d32",
          color: "white",
          padding: "40px 20px",
          textAlign: "center",
          marginTop: "50px",
        }}
      >
        <h3
          style={{
            fontSize: "24px",
            marginBottom: "12px",
            fontWeight: "600",
          }}
        >
          Learn More About Farming
        </h3>
        <p
          style={{
            fontSize: "16px",
            opacity: "0.9",
            maxWidth: "700px",
            margin: "0 auto",
          }}
        >
          Watch videos directly on our platform to stay updated with the latest
          farming techniques and agricultural innovations to improve your crop
          yield and farming efficiency in India.
        </p>
      </div>
    </div>
  );
};

export default FarmingVideos;